import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { focusableIn } from '../ui/focus';
import { AppHeader } from './AppHeader';
import { Breadcrumbs, type Crumb } from './Breadcrumbs';
import { Sidebar, type NavItem } from './Sidebar';

export interface AppShellProps {
  nav: readonly NavItem[];
  breadcrumbs?: readonly Crumb[] | undefined;
  /** Page title (Ace .page-header h1). */
  title?: ReactNode | undefined;
  titleActions?: ReactNode;
  headerCenter?: ReactNode;
  headerEnd?: ReactNode;
  /** Page footer (legacy: version, copyright, contact). */
  footer?: ReactNode;
  children: ReactNode;
  text?: Partial<typeof TEXT>;
}

const TEXT = {
  skip: 'Skip to main content',
  menu: 'Main menu',
  closeMenu: 'Close menu',
  collapse: 'Collapse sidebar',
  expand: 'Expand sidebar',
};

/**
 * Page chrome: skip link → header (banner) → sidebar (navigation) → breadcrumbs → main.
 * From `lg` the sidebar is docked and can collapse to icons; below `lg` it is an off-canvas
 * drawer (modal: focus moves in, Esc/backdrop close, focus returns to the menu button).
 */
export function AppShell({
  nav,
  breadcrumbs,
  title,
  titleActions,
  headerCenter,
  headerEnd,
  footer,
  children,
  text,
}: AppShellProps) {
  const t = { ...TEXT, ...text };
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerId = `drawer${useId().replace(/:/g, '')}`;
  useEffect(() => {
    if (!drawer) return;
    // Focus returns to the menu button itself, not to whatever was focused on open: Safari does not
    // focus a button on click, so there the previously focused element is <body>.
    const opener =
      document.querySelector<HTMLElement>(`[aria-controls="${drawerId}"]`) ??
      (document.activeElement as HTMLElement | null);
    const panel = drawerRef.current;
    if (panel) focusableIn(panel)[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawer(false);
      if (e.key === 'Tab' && panel) {
        const els = focusableIn(panel);
        const first = els[0];
        const last = els.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus();
    };
  }, [drawer, drawerId]);

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only z-(--z-toast) bg-surface px-3 py-2 text-link focus:not-sr-only focus:fixed focus:start-2 focus:top-2"
      >
        {t.skip}
      </a>
      <AppHeader
        onMenuClick={() => setDrawer(true)}
        menuOpen={drawer}
        menuControls={drawerId}
        menuLabel={t.menu}
        end={headerEnd}
      >
        {headerCenter}
      </AppHeader>

      <div className="flex flex-1">
        {/* docked sidebar (lg+) */}
        <div className="hidden shrink-0 border-e border-sidebar-border bg-sidebar lg:block">
          <div className="sticky top-(--spacing-navbar) h-[calc(100dvh-var(--spacing-navbar))]">
            <Sidebar
              items={nav}
              collapsed={collapsed}
              footer={
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => !c)}
                  aria-label={collapsed ? t.expand : t.collapse}
                  aria-pressed={collapsed}
                  title={collapsed ? t.expand : t.collapse}
                  className="flex h-8 w-full items-center justify-center text-sidebar-ink hover:text-sidebar-ink-hover"
                >
                  {collapsed ? (
                    <PanelLeftOpen aria-hidden="true" className="size-4" />
                  ) : (
                    <PanelLeftClose aria-hidden="true" className="size-4" />
                  )}
                </button>
              }
            />
          </div>
        </div>

        {/* off-canvas drawer (< lg) */}
        {drawer && (
          <div className="fixed inset-0 z-(--z-drawer) lg:hidden">
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-black/40"
              onClick={() => setDrawer(false)}
            />
            <div
              ref={drawerRef}
              id={drawerId}
              role="dialog"
              aria-modal="true"
              aria-label={t.menu}
              className="relative flex h-full w-72 max-w-[85vw] flex-col bg-sidebar shadow-modal"
            >
              <div className="flex h-navbar items-center justify-end bg-navbar px-2 on-dark">
                <button
                  type="button"
                  onClick={() => setDrawer(false)}
                  aria-label={t.closeMenu}
                  className="flex size-10 items-center justify-center text-ink-inverse hover:bg-navbar-hover"
                >
                  <X aria-hidden="true" className="size-5" />
                </button>
              </div>
              <Sidebar
                items={nav}
                fluid
                className="flex-1 border-e-0"
                onNavigate={() => setDrawer(false)}
              />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          {breadcrumbs && (
            <div className="flex min-h-breadcrumbs items-center border-b border-border bg-surface-muted px-3 sm:px-5">
              <Breadcrumbs items={breadcrumbs} />
            </div>
          )}
          <main
            id="main"
            tabIndex={-1}
            // at least a viewport tall: the footer starts below the fold and does not jump while
            // a page loads its data (layout shift)
            className="min-h-dvh flex-1 bg-surface px-3 pb-6 pt-2 outline-none sm:px-5"
          >
            {title && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-dotted border-border pb-3 pt-2">
                <h1
                  id="page-title"
                  tabIndex={-1}
                  className="text-3xl font-light text-heading outline-none"
                >
                  {title}
                </h1>
                {titleActions && <div className="flex flex-wrap gap-2">{titleActions}</div>}
              </div>
            )}
            {children}
          </main>
          {footer}
        </div>
      </div>
    </div>
  );
}
